import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAtlas } from '../buffy/atlas';
import { canvasStyle, createPainter } from '../buffy/painter';
import { addSnapTarget, requestSnap } from '../buffy/pixelSnap';
import { buffyScale } from '../hero/layout';
import { SLOW } from './useReveal';
import '../buffy/buffy.css';
import './buffyPeek.css';
import { withBuffyGate } from '../buffy/buffyOff';

/* BuffyPeek: Buffy peeking out from BEHIND a section tile (owner 2026-09-30:
   "have buffy being playful ... sticking over the top in the kilroy was here
   kind of vibe ... then in the next one hes like poking out from the left
   side for assessments").

   Place it as the FIRST child of the <Tile>:
     <Tile ...>
       <BuffyPeek edge="top" reveal={reveal} paused={!live} />
   It sits at z-index -1 inside the tile's own stacking context (the tile is
   isolation: isolate), so it paints ABOVE the tile's drop shadow (which
   would tint him) and BELOW the wallpaper layer and every card: the part of
   Buffy inside the tile's box is really covered, the part past the edge
   shows. Nothing clips him, so no cut line can sit on a fraction of a pixel.

   edge="top"  (F1): Kilroy over the tile's top edge (clip peek-over, Codex
               2026-09-30; the ledge is its row 34). The horns rise, then his
               eyes; he pulls himself up as his hooves come over the edge and
               grip it (peek-over 1-2-3), looks left, right, blinks, taps each
               hoof, bobs, lets go and ducks. About 12.5 s a loop.
   edge="left" (F2): out from behind the tile's left side (clip peek-side;
               its card edge is column 23). An ear pokes out, then he leans
               out: head, one lens, half his smile and a hoof curled round the
               edge (peek-side 1-2-3); looks left, blinks, waves (6/7), leans
               back. His body and legs stay behind the tile. About 9 s.

   Only APPROVED frames, joined only through adjacent pairs (every beat
   change is REST <-> a gesture frame, or two frames that follow each other
   in the sheet's own approved order); nothing of his hidden parts ever shows
   (both proved by BuffyPeek.test.js, pixel by pixel, for every beat and
   every step of every move). No CSS rotation or skew. The motion is
   transform only: he moves in whole ART pixels (dev device px each), so
   every frame of every move lands on the device-pixel grid; the one
   fractional part (where the tile sits on the page) is removed by
   pixelSnap, like every other Buffy player.

   Where it fits (measured, never assumed; re-measured on resize, fonts,
   and the tile's own size changes): scale 3, else 2, else hidden.
     top:  a straight part of the top edge (never a corner), the peek box
           10 px clear of every text line and control outside the tile, and
           inside the section (clear of the neighbour section).
     left: the left gutter holds his full reach plus 16 px (the page's
           narrowest gutter); his head at the question card's height (the
           peek's middle at 36 % of the tile), his whole cell, hidden legs
           included, on the straight part of the left edge.
   When it does not fit (phones; the left one under 1316 px wide, where the
   gutter is 32-57 px), nothing renders and onActive(false) tells the
   section (F2 then hides its pause button: nothing else in it loops).

   reveal "wait": hidden behind the tile. "play": the loop, after `delay`
   (unscaled ms, x SLOW) so the section's own entrance plays first.
   "done" (?settled=1, reduced motion): the settled peek, still.
   paused: freeze where he is (off screen). */

const REST = ['rest', 0];
const over = (n) => ['peek-over', n];
const side = (n) => ['peek-side', n];

/* Beats in unscaled ms (x SLOW at run time). `at` = the art row (top) or
   column (left) that sits ON the tile's edge: rows/columns before it show.
   A beat with from/to moves one art px at a time; no `at` = keep. */
const TOP_BEATS = [
  { ms: 1500, f: REST, at: 11 }, // hidden (his first row is 12)
  { ms: 560, f: REST, from: 11, to: 20, ease: 'out' }, // the horns come up
  { ms: 480, f: REST }, // just horns and the top of his head
  { ms: 360, f: REST, from: 20, to: 29, ease: 'out' }, // eyes over the edge
  { ms: 600, f: REST }, // peering
  // he pulls himself up to the ledge (row 34) as his hooves come over it
  { ms: 240, f: over(1), from: 29, to: 32, ease: 'out' },
  { ms: 160, f: over(2), from: 32, to: 34, ease: 'out' },
  { ms: 420, f: over(3) }, // grip
  { ms: 800, f: over(5) }, // looks left
  { ms: 160, f: over(3) },
  { ms: 800, f: over(6) }, // looks right
  { ms: 260, f: over(3) },
  { ms: 120, f: over(7) }, // blink
  { ms: 320, f: over(3) },
  { ms: 160, f: over(8) }, // taps the right hoof
  { ms: 160, f: over(3) },
  { ms: 160, f: over(9) }, // taps the left hoof
  { ms: 400, f: over(3) },
  { ms: 200, f: over(4) }, // a little pull-up bob
  { ms: 380, f: over(3) },
  { ms: 100, f: over(2) }, // lets go
  { ms: 220, f: over(1), from: 34, to: 29, ease: 'in' },
  { ms: 420, f: REST },
  { ms: 260, f: REST, from: 29, to: 11, ease: 'in' }, // ducks
];

/* The card edge is column 23 for the lean frames (peek-side 3-7). REST and
   LEAN 1 stay at column 15 / 14 or less (their hanging hoof starts at
   column 15 / 14), LEAN 2 at 22 or less (its hip outline is at column 22,
   rows 49-50): so no leg, hip or hanging-hoof pixel is ever past the edge. */
const LEFT_BEATS = [
  { ms: 1500, f: REST, at: 10 }, // hidden (his first column is 12)
  { ms: 420, f: REST, from: 10, to: 15, ease: 'out' }, // an ear pokes out
  { ms: 520, f: REST },
  { ms: 100, f: side(1), at: 14 }, // lean 1
  { ms: 440, f: side(2), from: 14, to: 22, ease: 'out' }, // leans out, hoof round the edge
  { ms: 520, f: side(3), at: 23 }, // the peek
  { ms: 900, f: side(4) }, // looks left
  { ms: 200, f: side(3) },
  { ms: 120, f: side(5) }, // blink
  { ms: 300, f: side(3) },
  // a little wave with the near hoof
  { ms: 150, f: side(6) },
  { ms: 150, f: side(7) },
  { ms: 150, f: side(6) },
  { ms: 150, f: side(7) },
  { ms: 150, f: side(6) },
  { ms: 420, f: side(3) },
  { ms: 380, f: side(2), from: 22, to: 14, ease: 'in' }, // leans back
  { ms: 100, f: side(1), at: 14 },
  { ms: 160, f: REST, from: 14, to: 10, ease: 'in' }, // gone
];

function normalize(beats) {
  let at = beats[0].at;
  let start = 0;
  const out = beats.map((b) => {
    const from = b.from ?? b.at ?? at;
    const to = b.to ?? b.at ?? at;
    at = to;
    const n = { ...b, from, to, start };
    start += b.ms;
    return n;
  });
  return { beats: out, total: start };
}

/* Geometry per edge, in art px of the 64 px cell (from the sheets' opaque
   boxes). top: columns 10..53 (the gripping and tapping hooves), up to 23
   rows above the edge (the pull bob lifts his horns to row 11 with the edge
   on row 34). left: reach 21 columns out (edge 23, the waving hoof reaches
   column 2); the part that shows is rows 12..41; the whole cell, hidden
   legs included, is rows 12..55. */
export const PEEKS = {
  top: {
    ...normalize(TOP_BEATS), hide: 11, settled: { f: over(3), at: 34 }, colMin: 10, colMax: 54, above: 23, delay: 800,
  },
  left: {
    ...normalize(LEFT_BEATS),
    hide: 10,
    settled: { f: side(3), at: 23 },
    reach: 21,
    showMin: 12,
    showMax: 42,
    rowMin: 12,
    rowMax: 56,
    delay: 3400,
  },
};

const easeOf = (name, x) => (name === 'in' ? x * x : name === 'out' ? 1 - (1 - x) * (1 - x) : x);

/* The pose at loop-local unscaled ms `t`: { clip, frame, mirror, at }. */
export function poseAt(peek, t) {
  const { beats, total } = peek;
  const ms = Math.max(0, Math.min(total - 1e-6, t));
  let b = beats[beats.length - 1];
  for (let i = 0; i < beats.length; i += 1) {
    if (ms < beats[i].start + beats[i].ms) {
      b = beats[i];
      break;
    }
  }
  const x = b.ms > 0 ? (ms - b.start) / b.ms : 1;
  const at = b.from === b.to ? b.to : b.from + Math.round((b.to - b.from) * easeOf(b.ease, x));
  return { clip: b.f[0], frame: b.f[1], mirror: Boolean(b.mirror), at };
}

const currentDpr = () => (typeof window !== 'undefined' && window.devicePixelRatio) || 1;

/* The devicePixelRatio, kept current (browser zoom, another screen); the
   same watcher as buffy/BuffySprite.jsx. */
function useDpr() {
  const [dpr, setDpr] = useState(currentDpr);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    let mq = null;
    const onChange = () => {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onChange);
      setDpr(currentDpr());
      watch();
    };
    function watch() {
      mq = window.matchMedia(`(resolution: ${currentDpr()}dppx)`);
      if (mq.addEventListener) mq.addEventListener('change', onChange);
    }
    watch();
    window.addEventListener('resize', onChange);
    return () => {
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);
  return dpr;
}

/* What Buffy must never touch: every text line in the section's copy
   column, and every control in the section outside the tile. */
function obstaclesOf(section, tile) {
  const rects = [];
  const copy = section.querySelector('.cxs-feature-copy');
  if (copy && document.createRange) {
    const walker = document.createTreeWalker(copy, NodeFilter.SHOW_TEXT);
    const range = document.createRange();
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.nodeValue.trim() && !(n.parentElement && n.parentElement.closest('.cxs-sr'))) {
        range.selectNodeContents(n);
        Array.from(range.getClientRects()).forEach((r) => { if (r.width && r.height) rects.push(r); });
      }
    }
  }
  section.querySelectorAll('button, a[href], [role="button"]').forEach((el) => {
    // the section's own pause button sits under the tile and only shows
    // while he fits (F2): counting it would switch him off, then on again
    if (tile.contains(el) || el.classList.contains('cxs-loop-pause')) return;
    const r = el.getBoundingClientRect();
    if (r.width && r.height) rects.push(r);
  });
  return rects;
}

const hits = (box, rects, pad) => rects.some((r) => box.l < r.right + pad && box.r > r.left - pad && box.t < r.bottom + pad && box.b > r.top - pad);

/* The tile he hides behind: his parent (the usual place), or a sibling. */
const tileOf = (root) => root.closest('.cxs-tile') || (root.parentElement && root.parentElement.querySelector(':scope > .cxs-tile'));

/* Where he fits: { ok, key, s, dev, u, x0, y0 } in px from the component's
   own origin (key = the PEEKS entry). top: x0 = the cell's left, y0 = the
   tile's top edge. left: x0 = the tile's left edge, y0 = the cell's top.
   Tried in order: the bigger scale first, then (left) the full lean first. */
function fit(edge, root, dpr) {
  const tile = tileOf(root);
  const section = root.closest('section');
  if (!tile || !section) return { ok: false };
  const o = root.getBoundingClientRect();
  const t = tile.getBoundingClientRect();
  const sec = section.getBoundingClientRect();
  if (!t.width || !t.height) return { ok: false };
  const rad = parseFloat(getComputedStyle(tile).borderTopLeftRadius) || 0;
  const obstacles = obstaclesOf(section, tile);
  // the first hidden row/column starts 1 device px INSIDE the edge: Chrome
  // may paint a canvas 1 device px off its snapped box (every Buffy player
  // does, measured by qa/buffy-dpr-v3), and this way that can never lift a
  // 1 px line of his hidden part above the tile's edge
  const tuck = 1 / dpr;
  const tries = edge === 'top' ? [[3, 'top'], [2, 'top']] : [[3, 'left'], [2, 'left']];
  for (const [s, key] of tries) {
    const p = PEEKS[key];
    const { dev, u } = buffyScale(s, dpr);
    if (edge === 'top') {
      const half = ((p.colMax - p.colMin) / 2) * u;
      const mid = ((p.colMax + p.colMin) / 2) * u; // the visible middle, from the cell's left
      const lo = t.left + rad + 12 + half;
      const hi = t.right - rad - 12 - half;
      const top = t.top - p.above * u;
      // his hidden part (rows up to 55, at the hidden row 11) stays inside the tile
      const deep = (56 - p.hide) * u <= t.height - rad;
      if (hi >= lo && top >= sec.top + 8 && deep) {
        const pref = Math.min(hi, Math.max(lo, t.left + t.width * 0.74));
        for (let d = 0; d <= hi - lo + 8; d += 8) {
          const found = (d ? [pref + d, pref - d] : [pref]).find((cx) => cx >= lo && cx <= hi
            && !hits({ l: cx - half, r: cx + half, t: top, b: t.top }, obstacles, 10));
          if (found != null) {
            return { ok: true, key, s, dev, u, x0: Math.round(found - mid - o.left), y0: t.top - o.top + tuck };
          }
        }
      }
    } else {
      const reach = p.reach * u;
      // the part that shows centred at 36 % of the tile (the question card's
      // height), then held so his WHOLE cell (hidden legs included) stays
      // beside the straight edge: the round corners can never show a piece
      // of him (feet on the tile's bottom line did: a speck of hoof)
      const lo = t.top + rad + 4 - p.rowMin * u;
      const hi = t.bottom - rad - 4 - p.rowMax * u;
      const cellY = Math.min(hi, Math.max(lo, t.top + t.height * 0.36 - ((p.showMin + p.showMax) / 2) * u));
      const box = { l: t.left - reach, r: t.left, t: cellY + p.showMin * u, b: cellY + p.showMax * u };
      // his hidden part (columns up to 52, at the hidden column 10) stays inside the tile
      const deep = (53 - p.hide) * u <= t.width - rad;
      if (hi >= lo && box.l >= sec.left + 16 && deep && !hits(box, obstacles, 10)) {
        return { ok: true, key, s, dev, u, x0: t.left - o.left + tuck, y0: Math.round(cellY - o.top) };
      }
    }
  }
  return { ok: false };
}

const sameGeo = (a, b) => a.ok === b.ok && (!a.ok || (a.key === b.key && a.s === b.s && a.dev === b.dev && a.u === b.u
  && Math.abs(a.x0 - b.x0) < 0.01 && Math.abs(a.y0 - b.y0) < 0.01));

function BuffyPeek({
  edge = 'top', reveal = 'wait', paused = false, delay, onActive, className = '',
}) {
  const atlas = useAtlas();
  const dpr = useDpr();
  const [geo, setGeo] = useState({ ok: false });
  const peek = PEEKS[geo.key] || (edge === 'left' ? PEEKS.left : PEEKS.top);
  const lead = delay ?? peek.delay;
  const rootRef = useRef(null);
  const moverRef = useRef(null);
  const cellRef = useRef(null);
  const canvasRef = useRef(null);
  const painterRef = useRef(null);
  // played loop time (unscaled ms, includes the lead-in); survives pauses
  const clock = useRef(0);
  const shown = useRef('');

  // measure where he fits (and re-measure whenever anything could move)
  const measure = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const next = fit(edge, root, dpr);
    setGeo((g) => (sameGeo(g, next) ? g : next));
  }, [edge, dpr]);

  useLayoutEffect(() => { measure(); }, [measure]);

  useEffect(() => {
    const root = rootRef.current;
    const tile = root && tileOf(root);
    const section = root && root.closest('section');
    let ro = null;
    let raf = 0;
    const later = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    if (typeof ResizeObserver !== 'undefined' && tile) {
      ro = new ResizeObserver(later);
      ro.observe(tile);
      if (tile.parentElement) ro.observe(tile.parentElement);
      const copy = section && section.querySelector('.cxs-feature-copy');
      if (copy) ro.observe(copy);
    }
    window.addEventListener('resize', later);
    let alive = true;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (alive) later(); });
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', later);
    };
  }, [measure]);

  useEffect(() => {
    if (onActive) onActive(geo.ok);
  }, [geo.ok, onActive]);

  // show one pose: the cell's place (whole art px from the edge) + its frame
  const show = useCallback((pose) => {
    const mover = moverRef.current;
    const painter = painterRef.current;
    if (!geo.ok || !mover || !painter) return;
    const x = edge === 'top' ? geo.x0 : geo.x0 - pose.at * geo.u;
    const y = edge === 'top' ? geo.y0 - pose.at * geo.u : geo.y0;
    const tf = `translate3d(${x}px, ${y}px, 0)`;
    if (tf !== shown.current) {
      shown.current = tf;
      mover.style.transform = tf;
      mover.setAttribute('data-at', String(pose.at));
    }
    painter.draw(pose.clip, pose.frame, pose.mirror);
  }, [edge, geo]);

  const still = reveal === 'done';
  const poseNow = useCallback(() => {
    if (still) return { clip: peek.settled.f[0], frame: peek.settled.f[1], mirror: false, at: peek.settled.at };
    const t = clock.current;
    if (reveal !== 'play' || t < lead) return { clip: 'rest', frame: 0, mirror: false, at: peek.hide };
    return poseAt(peek, (t - lead) % peek.total);
  }, [still, reveal, lead, peek]);

  // the painter, at the device scale; then the current pose
  useLayoutEffect(() => {
    if (!geo.ok || !canvasRef.current) return;
    // a new canvas (he was hidden, then fits again) gets a new painter
    if (!painterRef.current || painterRef.current.canvas !== canvasRef.current) {
      painterRef.current = Object.assign(createPainter(canvasRef.current), { canvas: canvasRef.current });
    }
    painterRef.current.scale(geo.dev, dpr);
    painterRef.current.invalidate();
    shown.current = '';
    show(poseNow());
    requestSnap();
  }, [geo, dpr, atlas, show, poseNow]);

  // the painted cell on the device-pixel grid (removes the page's fraction)
  useLayoutEffect(() => {
    const el = cellRef.current;
    if (!geo.ok || !el) return undefined;
    let sx = 0;
    let sy = 0;
    return addSnapTarget({
      origin() {
        if (!el.isConnected) return null;
        const r = el.getBoundingClientRect();
        return { x: r.left - sx, y: r.top - sy };
      },
      apply(x, y) {
        if (x === sx && y === sy) return;
        sx = x;
        sy = y;
        el.style.translate = x || y ? `${x}px ${y}px` : '';
      },
    });
  }, [geo.ok]);

  // the loop: runs only while playing, on screen and not paused
  useEffect(() => {
    if (still || reveal !== 'play' || paused || !geo.ok) return undefined;
    let raf = 0;
    let last = null;
    const tick = (now) => {
      if (last !== null) clock.current += Math.min(now - last, 100) / SLOW;
      last = now;
      show(poseNow());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [still, reveal, paused, geo.ok, show, poseNow]);

  const u = geo.u || 1;
  return (
    <span ref={rootRef} className={`cxs-peek is-${edge} ${className}`} data-active={geo.ok ? 'true' : 'false'} aria-hidden="true">
      {geo.ok ? (
        <span ref={moverRef} className="cxs-peek-mover" data-scale={geo.s} data-variant={geo.key}>
          <span ref={cellRef} className="bx-player-cell cxs-peek-cell" style={{ width: 64 * u, height: 64 * u }}>
            <canvas ref={canvasRef} className="bx-canvas" width={64 * geo.dev} height={64 * geo.dev} style={canvasStyle(geo.dev, dpr)} />
          </span>
        </span>
      ) : null}
    </span>
  );
}

// a page can take Buffy off (buffy/buffyOff.js)
export default withBuffyGate(BuffyPeek);
